import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { reportsAPI, cashierAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  DollarSign, TrendingUp, Users, Receipt, ArrowUpRight, ArrowDownRight,
  CreditCard, ChefHat, Boxes, Heart, Map, ClipboardList, Settings2,
  Lock, Unlock, RefreshCw, BarChart3
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from 'recharts';
import { sidebarItems } from './DashboardLayout';

const DashboardOverview = () => {
  const { currentOutlet, isOwner, isManager, isPlatformAdmin, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [xReading, setXReading] = useState(null);
  const [dailySales, setDailySales] = useState([]);
  const [daypartData, setDaypartData] = useState([]);
  const [dayStatus, setDayStatus] = useState({ is_locked: false, business_date: null });

  useEffect(() => { if (currentOutlet) loadData(); }, [currentOutlet]);

  const loadData = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const [xRes, dailyRes, daypartRes, dayStatusRes] = await Promise.all([
        reportsAPI.getXReading(currentOutlet.id).catch(() => ({ data: null })),
        reportsAPI.getDailySales(currentOutlet.id, weekAgo, today).catch(() => ({ data: [] })),
        reportsAPI.getDaypart(currentOutlet.id, today).catch(() => ({ data: [] })),
        cashierAPI.getDayStatus(currentOutlet.id).catch(() => ({ data: { is_locked: false } }))
      ]);
      setXReading(xRes.data);
      setDailySales(Array.isArray(dailyRes.data) ? dailyRes.data : []);
      setDaypartData(Array.isArray(daypartRes.data) ? daypartRes.data : []);
      setDayStatus(dayStatusRes.data || { is_locked: false });
    } catch (error) {
      console.error('Dashboard data load error:', error);
    } finally { setLoading(false); }
  };

  // Derive the quick-access tiles from the SAME source as the sidebar so the two
  // never drift (single source of truth = sidebarItems, role-filtered).
  const userRole = isPlatformAdmin ? 'Super Admin' : (user?.role_name || user?.role || '');
  const moduleCards = sidebarItems.filter(
    (i) => !i.divider && i.path && i.path !== '/dashboard' && i.path !== '/platform/admin'
      && (i.always || (i.roles && i.roles.includes(userRole)))
  );

  const stats = [
    { title: "Today's Sales", value: `₱${(xReading?.totals?.net_sales || 0).toLocaleString()}`, change: '+12%', positive: true, icon: DollarSign },
    { title: 'Transactions', value: xReading?.totals?.transaction_count || 0, change: '+8%', positive: true, icon: Receipt },
    { title: 'Guests', value: xReading?.totals?.guest_count || 0, change: '+5%', positive: true, icon: Users },
    { title: 'Avg Ticket', value: `₱${(xReading?.totals?.average_ticket || 0).toFixed(2)}`, change: '-2%', positive: false, icon: BarChart3 },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-16 bg-card rounded-xl border border-border animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-24 bg-card rounded-xl border border-border animate-pulse" />)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-32 bg-card rounded-xl border border-border animate-pulse" />)}
        </div>
        <div className="h-80 bg-card rounded-xl border border-border animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-overview">
      {/* Status Banner */}
      <Card className="shadow-card">
        <CardContent className="flex flex-wrap items-center gap-4 p-4">
          {dayStatus.is_locked ? (
            <span className="flex items-center gap-2 text-destructive font-semibold">
              <Lock className="w-4 h-4" /> Day Locked (Z-Reading done)
            </span>
          ) : (
            <span className="flex items-center gap-2 text-secondary font-semibold">
              <Unlock className="w-4 h-4" /> Day Open
            </span>
          )}
          {dayStatus.business_date && (
            <span className="text-muted-foreground text-sm">Business Date: {new Date(dayStatus.business_date).toLocaleDateString()}</span>
          )}
          <Button size="sm" onClick={loadData} className="ml-auto bg-primary hover:bg-primary/90 text-white" data-testid="dashboard-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </CardContent>
      </Card>

      {/* Quick Access Modules (navy tiles) */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {moduleCards.map((mod) => {
          const Icon = mod.icon;
          return (
            <Link
              key={mod.path}
              to={mod.path}
              className="flex flex-col items-center justify-center gap-2 p-4 bg-primary text-primary-foreground rounded-xl transition-all duration-200 hover:-translate-y-1 hover:shadow-elevated group"
              data-testid={`module-${mod.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="w-11 h-11 rounded-lg bg-white/10 flex items-center justify-center group-hover:bg-secondary transition-colors">
                <Icon className="w-5 h-5" />
              </div>
              <span className="text-xs text-center font-medium leading-tight">{mod.label}</span>
            </Link>
          );
        })}
      </div>

      {/* KPI Cards (teal) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              className="relative overflow-hidden rounded-xl p-6 text-white shadow-card bg-gradient-to-br from-teal to-teal-dark"
              data-testid={`stat-card-${index}`}
            >
              <div className="flex items-center justify-between mb-5">
                <div className="w-11 h-11 rounded-lg bg-white/15 flex items-center justify-center">
                  <Icon className="w-5 h-5" />
                </div>
                <span className="text-xs flex items-center gap-0.5 bg-white/15 px-2 py-1 rounded-full font-semibold">
                  {stat.positive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {stat.change}
                </span>
              </div>
              <p className="text-sm text-white/80">{stat.title}</p>
              <p className="font-heading text-4xl font-extrabold mt-1 tracking-tight">{stat.value}</p>
            </div>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="shadow-card">
          <CardHeader><CardTitle className="font-heading text-lg text-foreground">Sales Trend (7 Days)</CardTitle></CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dailySales}>
                  <defs>
                    <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1A9E8F" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#1A9E8F" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="date" stroke="#94a3b8" tick={{ fill: '#64748b', fontSize: 12 }}
                    tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { weekday: 'short' })} />
                  <YAxis stroke="#94a3b8" tick={{ fill: '#64748b', fontSize: 12 }} tickFormatter={(val) => `₱${(val / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', color: '#5F0E15' }}
                    formatter={(value) => [`₱${value.toLocaleString()}`, 'Sales']} />
                  <Area type="monotone" dataKey="net_sales" stroke="#1A9E8F" fill="url(#salesGradient)" strokeWidth={2.5} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardHeader><CardTitle className="font-heading text-lg text-foreground">Sales by Daypart</CardTitle></CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={daypartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="daypart" stroke="#94a3b8" tick={{ fill: '#64748b', fontSize: 12 }} />
                  <YAxis stroke="#94a3b8" tick={{ fill: '#64748b', fontSize: 12 }} tickFormatter={(val) => `₱${(val / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', color: '#5F0E15' }}
                    formatter={(value) => [`₱${value.toLocaleString()}`, 'Sales']} cursor={{ fill: 'rgba(26,158,143,0.08)' }} />
                  <Bar dataKey="sales" fill="#5F0E15" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payment Breakdown */}
      <Card className="shadow-card">
        <CardHeader><CardTitle className="font-heading text-lg text-foreground">Payment Methods</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(xReading?.totals?.payments || {}).map(([method, amount]) => (
              <div key={method} className="p-4 bg-muted rounded-lg border border-border">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">{method}</p>
                <p className="font-mono text-xl font-bold text-foreground mt-1">₱{(amount || 0).toLocaleString()}</p>
              </div>
            ))}
            {!Object.keys(xReading?.totals?.payments || {}).length && (
              <p className="text-sm text-muted-foreground col-span-full text-center py-4">No payment data yet today.</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardOverview;
